import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import NotificationAlert from "../../../../components/ui/alerts/notifications/NotificationAlert";
import LoadingPlaceholder from "../../../../components/ui/loading/LoadingPlaceholder";

function CheckoutFormBody({
  confirmIntent,
  returnUrl,
  onFormReady,
  isFreePlan = false,
  isTestAccount = false,
}) {
  // Call hooks unconditionally at the top level
  const stripe = useStripe();
  const elements = useElements();

  const [formProcessing, setFormProcessing] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();

    setFormProcessing(true);
    setFormError(null);

    try {
      if (!isTestAccount && (!stripe || !elements)) return;

      const result = await confirmIntent({
        stripe,
        elements,
        returnUrl,
        isTestAccount,
      });

      if (result?.error) {
        setFormError(result.error.message);
      }
    } catch (error) {
      setFormError("Unexpected payment processing exception.");
    } finally {
      setFormProcessing(false);
    }
  };

  const isSubmitDisabled =
    formProcessing || (!isTestAccount && (!stripe || !elements));

  const getButtonText = () => {
    if (isTestAccount) return "COMPLETE TEST SUBSCRIPTION";
    if (isFreePlan) return "CONFIRM CARD";
    return "PAY";
  };

  return (
    <form onSubmit={handleCheckoutSubmit}>
      {formError && (
        <div style={{ marginBottom: "16px" }}>
          <NotificationAlert
            alertType="error"
            alertTitle={isTestAccount ? "Bypass Failed" : "Validation Failed"}
            alertDescription={formError}
          />
        </div>
      )}

      {formProcessing && (
        <div style={{ marginBottom: "16px" }}>
          <LoadingPlaceholder rows={1} type="card-large" />
        </div>
      )}

      <div style={{ display: formProcessing ? "none" : "block" }}>
        {!isTestAccount ? (
          <PaymentElement
            options={{ layout: "accordion" }}
            onReady={onFormReady}
          />
        ) : (
          <div
            style={{
              backgroundColor: "#EBF5FF",
              borderLeft: "4px solid #006CD1",
              padding: "16px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                fontSize: "14px",
                color: "#004085",
                fontWeight: "600",
                margin: "0 0 4px 0",
              }}
            >
              Test Mode Active
            </p>
            <p style={{ fontSize: "13px", color: "#40464b", margin: 0 }}>
              Payment verification is waived for test accounts.
            </p>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginTop: "24px",
          }}
        >
          <button
            type="submit"
            className="cta-button primary checkout-pay-button"
            disabled={isSubmitDisabled}
          >
            <b>{getButtonText()}</b>
          </button>

          {isFreePlan && !isTestAccount && (
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderLeft: "4px solid #6c757d",
                padding: "12px 16px",
                borderRadius: "4px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "#475467",
                  lineHeight: "150%",
                  margin: 0,
                }}
              >
                <strong>Why do we need a card?</strong> Access to the Free Tier
                requires verification to prevent automated bot sign-ups. Your
                card will not be charged today, and no subscription will be
                created.
              </p>
            </div>
          )}

          {!isTestAccount && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/lock_dark.svg`}
                alt="Card Icon"
                style={{ width: "14px", height: "18px" }}
              />
              <span
                style={{
                  color: "#40464b",
                  fontSize: "12px",
                  fontWeight: "500",
                  lineHeight: "140%",
                  fontStyle: "normal",
                }}
              >
                {isFreePlan
                  ? "Identity verified securely through Stripe"
                  : "Payments are securely processed through Stripe"}
              </span>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}

export default CheckoutFormBody;
